import { DateType, DateTypeDisplay, RankMethod } from '@/pages/EnergyAnalysisRanking/utils';
import { DatePicker, Form, message, Radio, Modal } from '@maxtropy/components';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomerRequest } from '../..';

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

const RangePicker = DatePicker.RangePicker;

interface CustomDateModalProps {
  onOk: (params: any) => void;
  onCancel: () => void;
  rankMethod: RankMethod;
  customerRequest?: CustomerRequest;
}

const CustomDateModal = ({ onOk, onCancel, customerRequest, rankMethod }: CustomDateModalProps) => {
  const [form] = Form.useForm();
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [value, setValue] = useState<RangeValue>();
  const [comparisonValue, setComparisonValue] = useState<RangeValue>();

  useEffect(() => {
    if (customerRequest) {
      setTimeResolution(customerRequest.timeResolution);
      setValue(customerRequest.currentPeriod);
      setComparisonValue(customerRequest.comparisonPeriod);
      form.setFieldsValue({
        currentPeriod: customerRequest.currentPeriod,
        comparisonPeriod: customerRequest.comparisonPeriod,
      });
    }
  }, []);

  const handleOk = () => {
    form.validateFields().then(values => {
      onOk({ ...values, timeResolution });
    });
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    const value: DateType = e.target.value;
    setTimeResolution?.(value);
    form.setFieldsValue({
      currentPeriod: null,
      comparisonPeriod: null,
    });
    setValue(null);
    setComparisonValue(null);
  };

  const disabledDate = (current: Dayjs) => {
    if (timeResolution === DateType.DAY) {
      return current && current > dayjs().startOf('day');
    } else {
      let tooLate = null;
      let tooEarly = null;
      const afterToday = current && current > dayjs().endOf('day');
      if (DateType.MONTH === timeResolution) {
        tooLate = value?.[0] && current.diff(value?.[0], 'months') >= 23;
        tooEarly = value?.[1] && value?.[1].diff(current, 'months') >= 23;
      }
      return !!tooEarly || !!tooLate || !!afterToday;
    }
  };

  const onChangePicker = (dates: RangeValue, type: number) => {
    let isFlag = true;
    switch (timeResolution) {
      case DateType.DAY:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(30, 'day').valueOf() < dates?.[1].valueOf()) {
          // test返回上一次的值
          form.setFieldsValue({
            test: [dates?.[0], dates?.[1]],
          });
          isFlag = false;
          message.error('最多选择31天');
        }
        break;
      case DateType.WEEK:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(10, 'week').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择10周');
          isFlag = false;
        }
        break;
      case DateType.MONTH:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(24, 'month').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择24个月');
          isFlag = false;
        }
        break;
      case DateType.YEAR:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(6, 'year').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择6年');
          isFlag = false;
        }
        break;
    }

    if (isFlag) {
      if (type === 1) {
        setValue(dates);
      } else {
        setComparisonValue(dates);
      }
    } else {
      form.setFieldsValue({
        test: value,
      });
    }
  };

  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.WEEK) {
      return 'week';
    } else if (timeResolution === DateType.YEAR) {
      return 'year';
    } else {
      return 'date';
    }
  }, [timeResolution]);

  return (
    <Modal title="自定义维度设置" open={true} contentClassName="modal-form-content" onOk={handleOk} onCancel={onCancel}>
      <Form form={form}>
        <Radio.Group
          onChange={onRadioChange}
          value={timeResolution}
          buttonStyle="solid"
          style={{ marginLeft: 136, marginBottom: 24 }}
        >
          <Radio.Button value={DateType.DAY}>按{DateTypeDisplay[DateType.DAY]}</Radio.Button>
          <Radio.Button value={DateType.WEEK}>按{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
          <Radio.Button value={DateType.MONTH}>按{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
          <Radio.Button value={DateType.YEAR}>按{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
        </Radio.Group>
        <Form.Item
          label="本期用能范围"
          name="currentPeriod"
          rules={[{ required: true, message: '请选择本期用能范围' }]}
        >
          <RangePicker
            picker={picker}
            style={{ width: '100%' }}
            value={value}
            disabledDate={disabledDate}
            onChange={dates => onChangePicker(dates, 1)}
            allowClear={true}
          />
        </Form.Item>
        <Form.Item
          label="对比周期范围"
          name="comparisonPeriod"
          rules={[{ required: rankMethod === RankMethod.BY_INCREMENT, message: '请选择对比周期期用能范围' }]}
        >
          <RangePicker
            picker={picker}
            style={{ width: '100%' }}
            value={comparisonValue}
            disabledDate={disabledDate}
            onChange={dates => onChangePicker(dates, 2)}
            allowClear={true}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
export default CustomDateModal;
